import _objectSpread from "/usr/app/node_modules/@babel/runtime/helpers/esm/objectSpread2.js";
import _regenerator from "/usr/app/node_modules/@babel/runtime/helpers/esm/regenerator.js";
import _asyncToGenerator from "/usr/app/node_modules/@babel/runtime/helpers/esm/asyncToGenerator.js";
import axios from "axios";
import { extractLangFromReq, invalidateUser, is422, isUnauthorized } from "@/common/axiosInstance";
import { awaitTimeout, getUniversalCookieValue, getUserAgent } from "@/utils/GlobalJavascriptFunction";
import { apiUrl, ENABLE_PING_SECURITY, SYARAH_V2_JWT_SECRET } from "@globalConfig";
import { createToken, validateJwtPayloadValue } from "@/utils/jwt";
import { ValidationException } from "@/common/exceptions/ValidationException";
import SHA256 from "crypto-js/sha256";
import useLocalStorage from "@hooks/useLocalStorage";
import { checkTestFlag } from "@/common/config/websiteConfig";
import getInAppSource from "@/common/getInAppSource";
var BASE_PATH = 'syarah_v2';
var lastPing = null;
var isValidToken = function isValidToken(response) {
  if (ENABLE_PING_SECURITY && response !== null && response !== void 0 && response.headers) {
    var pongToken = response.headers['x-pong'];
    if (!pongToken) {
      console.error("Invalid token received from server");
      return false;
    }
    var challenge = SHA256(lastPing + '/' + SYARAH_V2_JWT_SECRET).toString().substring(0, 7);
    return validateJwtPayloadValue(pongToken, 'challenge', challenge);
  }
  return true;
};
var createSyarahV2Fetcher = function createSyarahV2Fetcher() {
  var _ref = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
    _ref$throwValidationE = _ref.throwValidationException,
    throwValidationException = _ref$throwValidationE === void 0 ? false : _ref$throwValidationE;
  var axiosInstanceSyarahV2 = createAxiosInstance();
  axiosInstanceSyarahV2.interceptors.response.use(/*#__PURE__*/function () {
    var _ref2 = _asyncToGenerator(/*#__PURE__*/_regenerator().m(function _callee(response) {
      return _regenerator().w(function (_context) {
        while (1) switch (_context.n) {
          case 0:
            _context.n = 1;
            return awaitTimeout(window.API_TIMEOUT || 0);
          case 1:
            if (!(__IS_CSR__ && checkTestFlag("TEST_DELAY_API_TIME__3000"))) {
              _context.n = 2;
              break;
            }
            _context.n = 2;
            return awaitTimeout(3000);
          case 2:
            if (!(__IS_CSR__ && checkTestFlag("TEST_DELAY_API_TIME__60000"))) {
              _context.n = 3;
              break;
            }
            _context.n = 3;
            return awaitTimeout(60000);
          case 3:
            if (!(__IS_CSR__ && checkTestFlag("TEST_API_500"))) {
              _context.n = 4;
              break;
            }
            console.log("Simulated 500 Internal Server Error (syarah_v2)");
            throw new Error("Simulated 500 Internal Server Error (syarah_v2)");
          case 4:
            if (!(isUnauthorized(response) || !isValidToken(response))) {
              _context.n = 5;
              break;
            }
            return _context.a(2, invalidateUser());
          case 5:
            return _context.a(2, response);
        }
      }, _callee);
    }));
    return function (_x) {
      return _ref2.apply(this, arguments);
    };
  }(), function (error) {
    console.error("-> Axios error2", error);
    var _useLocalStorage = useLocalStorage(),
      getLocalStorage = _useLocalStorage.getLocalStorage,
      setLocalStorage = _useLocalStorage.setLocalStorage;
    if (isUnauthorized(error === null || error === void 0 ? void 0 : error.response)) {
      invalidateUser();
    }
    if (__IS_CSR__ && error !== null && error !== void 0 && error.response) {
      var serverTimeDiff = error === null || error === void 0 ? void 0 : error.response.headers['x-server-time-adjust'];
      if (serverTimeDiff) {
        setLocalStorage('serverTimeDiff', String(parseFloat(getLocalStorage('serverTimeDiff') || '0') + parseFloat(serverTimeDiff || '0')));
      }
    }
    if (error !== null && error !== void 0 && error.response && is422(error === null || error === void 0 ? void 0 : error.response)) {
      if (throwValidationException) {
        return Promise.reject(ValidationException.factoryFromAxios(error));
      }
      return error.response;
    }
    return Promise.resolve(error);
  });
  axiosInstanceSyarahV2.interceptors.request.use(/*#__PURE__*/function () {
    var _ref3 = _asyncToGenerator(/*#__PURE__*/_regenerator().m(function _callee2(config) {
      var fullUrl, paramsString, data, userId, access_token, token;
      return _regenerator().w(function (_context2) {
        while (1) switch (_context2.n) {
          case 0:
            if (!__IS_SSR__) {
              _context2.n = 1;
              break;
            }
            config.headers['x-server-data'] = process.env.SECRET_API_V2_SERVER_TOKEN || "5756A6628BC663AA783E76BE35296";
            return _context2.a(2, config);
          case 1:
            fullUrl = '/' + BASE_PATH + (config.url.startsWith('/') ? config.url : '/' + config.url);
            if (config.params) {
              paramsString = new URLSearchParams(config.params).toString();
              if (paramsString) {
                fullUrl += (fullUrl.includes('?') ? '&' : '?') + paramsString;
              }
            }
            data = config.transformRequest[0](config.data, config.headers) || {};
            userId = getUniversalCookieValue(null, "userId", "guest");
            access_token = getUniversalCookieValue(null, "accessToken");
            token = createToken({
              audience: 'web',
              data: {
                body: config.data ? JSON.parse(data) : null,
                url: fullUrl
              },
              sub: userId,
              access_token: access_token
            });
            if (ENABLE_PING_SECURITY) {
              config.headers['x-ping'] = lastPing = Math.random().toString(36).substring(7);
            }
            if (token) {
              config.headers['Authorization'] = "Bearer ".concat(token);
            }
            return _context2.a(2, config);
        }
      }, _callee2);
    }));
    return function (_x2) {
      return _ref3.apply(this, arguments);
    };
  }(), function (error) {
    console.log("-> error", error);
    return Promise.reject(error);
  });
  return axiosInstanceSyarahV2;
};
export var createAxiosInstance = function createAxiosInstance() {
  var UUID = getUniversalCookieValue(null, "UUID", "UA-" + getUserAgent(null));
  var gbuuid = getUniversalCookieValue(null, "gbuuid", "".concat(new Date().getTime(), "-").concat(Math.floor(Math.random() * 100000)));
  var inAppSource = getInAppSource(null);
  var inAppSourceData = {};
  if (inAppSource && inAppSource.platform) {
    inAppSourceData = {
      'webview-app-source': inAppSource.platform,
      'webview-app-version': inAppSource.version || "unknown"
    };
  }
  var baseURL = apiUrl.replace("syarah_v1", "syarah_v2");
  // get Ip address from express request, or cloudflare headers
  var remoteIp = null;
  var headers = _objectSpread(_objectSpread({
    "Content-Type": "application/json",
    "Accept-Language": extractLangFromReq(),
    Accept: "application/json",
    "user-id": UUID,
    "gbuuid": gbuuid,
    device: "web"
  }, remoteIp ? {
    "x-secret-forwarded-ip": remoteIp
  } : {}), inAppSourceData);
  return axios.create({
    baseURL: baseURL,
    headers: headers
  });
};
export default createSyarahV2Fetcher;